import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Snackbar,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import dayjs from "dayjs";
import useWeeklyStaffScheduling from "../hooks/useWeeklyStaffScheduling";
import { saveWeeklyStaffSchedulingBulk } from "../api/staffSchedulingApi";
import CreateStaffDialog from "../jsx/dialogs/CreateStaffDialog";
import { mondayOfWeek } from "../utils/timetableUtils";

function formatDisplayDate(value) {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM") : value;
}

function formatDayHeader(value) {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format("ddd D");
}

function buildDateSet(dates = []) {
  return new Set((dates || []).map((d) => dayjs(d).format("YYYY-MM-DD")));
}

function isWithinStay(day, arrivalDate, departureDate) {
  const current = dayjs(day);
  const arrival = dayjs(arrivalDate);
  const departure = dayjs(departureDate);

  if (!current.isValid() || !arrival.isValid() || !departure.isValid()) return false;

  return (
    current.isSame(arrival, "day") ||
    current.isSame(departure, "day") ||
    (current.isAfter(arrival, "day") && current.isBefore(departure, "day"))
  );
}

function arraysEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  return aSorted.every((value, index) => value === bSorted[index]);
}

function sortSchedulingRows(rows = []) {
  return [...rows].sort((a, b) => {
    const aStaff = Boolean(a.staff);
    const bStaff = Boolean(b.staff);

    if (aStaff !== bStaff) {
      return aStaff ? -1 : 1;
    }

    return String(a.name || "").localeCompare(String(b.name || ""));
  });
}

export default function StaffSchedulingPage({
  selectedDate,
  selectedCampusName,
  onSelectAdult,
  campuses = [],
}) {
  const weekStart = useMemo(() => {
    const monday = mondayOfWeek(selectedDate);
    return monday ? monday.format("YYYY-MM-DD") : "";
  }, [selectedDate]);

  const {
    data,
    loading,
    error,
    refresh,
  } = useWeeklyStaffScheduling(weekStart, selectedCampusName, {
    enabled: Boolean(selectedCampusName && weekStart),
  });

  const [draftDatesByNsrId, setDraftDatesByNsrId] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
    adultId: null,
  });

  useEffect(() => {
    const next = {};
    (data?.rows || []).forEach((row) => {
      next[row.nsrId] = (row.scheduledDates || []).map((d) =>
        dayjs(d).format("YYYY-MM-DD")
      );
    });
    setDraftDatesByNsrId(next);
  }, [data?.rows]);

  const days = data?.days || [];
  const rows = useMemo(() => sortSchedulingRows(data?.rows || []), [data?.rows]);

  const staffRows = useMemo(
    () => rows.filter((row) => row.staff),
    [rows]
  );

  const nonStaffRows = useMemo(
    () => rows.filter((row) => !row.staff),
    [rows]
  );

  const changedStaffRows = useMemo(() => {
    return staffRows.filter((row) => {
      const originalDates = (row.scheduledDates || []).map((d) =>
        dayjs(d).format("YYYY-MM-DD")
      );
      const draftDates = draftDatesByNsrId[row.nsrId] || originalDates;
      return !arraysEqual(originalDates, draftDates);
    });
  }, [staffRows, draftDatesByNsrId]);

  const hasChanges = changedStaffRows.length > 0;

  const showToast = (message, severity = "success", extra = {}) => {
    setToast({
      open: true,
      severity,
      message,
      adultId: extra?.adultId || null,
    });
  };

  const closeToast = () => {
    setToast((prev) => ({
      ...prev,
      open: false,
      adultId: null,
    }));
  };

  const handleOpenCreateStaff = () => {
    setCreateDialogOpen(true);
  };

  const handleCloseCreateStaff = () => {
    setCreateDialogOpen(false);
  };

  const handleStaffCreated = async () => {
    setCreateDialogOpen(false);
    await refresh();
    showToast("Staff member created", "success");
  };

  const toggleDate = (nsrId, day) => {
    const dayKey = dayjs(day).format("YYYY-MM-DD");

    setDraftDatesByNsrId((prev) => {
      const current = new Set(prev[nsrId] || []);
      if (current.has(dayKey)) {
        current.delete(dayKey);
      } else {
        current.add(dayKey);
      }

      return {
        ...prev,
        [nsrId]: Array.from(current).sort(),
      };
    });
  };

  // Fill every staff row with all the days that fall within that person's stay,
  // so the user can start from "all on" and just untick the few who are off.
  // Draft-only — still needs "Save all changes" to persist.
  const handleCheckAllAvailable = () => {
    setDraftDatesByNsrId((prev) => {
      const next = { ...prev };
      staffRows.forEach((row) => {
        next[row.nsrId] = days
          .filter((day) => isWithinStay(day, row.arrivalDate, row.departureDate))
          .map((day) => dayjs(day).format("YYYY-MM-DD"))
          .sort();
      });
      return next;
    });
  };

  const handleClearAll = () => {
    setDraftDatesByNsrId((prev) => {
      const next = { ...prev };
      staffRows.forEach((row) => {
        next[row.nsrId] = [];
      });
      return next;
    });
  };

  const handleSaveAll = async () => {
    if (!hasChanges) return;

    const rowsToSave = changedStaffRows.map((row) => ({
      nsrId: row.nsrId,
      dates: (draftDatesByNsrId[row.nsrId] || []).slice().sort(),
    }));

    setSavingAll(true);

    try {
      await saveWeeklyStaffSchedulingBulk({
        weekStart,
        rows: rowsToSave,
      });
      await refresh();
      showToast(
        `Saved ${rowsToSave.length} staff row${rowsToSave.length === 1 ? "" : "s"}`,
        "success"
      );
    } catch (err) {
      showToast(err?.message || "Failed to save staff schedules", "error");
    } finally {
      setSavingAll(false);
    }
  };

  if (!selectedCampusName) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Select a campus to view weekly staff scheduling.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography color="error">{error}</Typography>
          <Box>
            <Chip label="Retry" onClick={refresh} clickable />
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Staff Scheduling
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCampusName} · Week of {formatDisplayDate(data?.weekStart)}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
              <Chip label={`${rows.length} row${rows.length === 1 ? "" : "s"}`} />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`${staffRows.length} staff`}
              />
              {nonStaffRows.length > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${nonStaffRows.length} non-staff`}
                />
              )}

              <Button
                variant="outlined"
                onClick={handleOpenCreateStaff}
              >
                Create staff
              </Button>

              <Button
                variant="outlined"
                onClick={handleCheckAllAvailable}
                disabled={savingAll || staffRows.length === 0}
              >
                Check all available
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                onClick={handleClearAll}
                disabled={savingAll || staffRows.length === 0}
              >
                Clear all
              </Button>

              <Button
                variant="contained"
                onClick={handleSaveAll}
                disabled={!hasChanges || savingAll}
              >
                {savingAll
                  ? "Saving..."
                  : hasChanges
                  ? `Save all changes (${changedStaffRows.length})`
                  : "Save all"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Roles</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Arrival</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Departure</TableCell>
                {days.map((day) => (
                  <TableCell key={day} align="center" sx={{ minWidth: 80 }}>
                    {formatDayHeader(day)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row) => {
                const originalDates = (row.scheduledDates || []).map((d) =>
                  dayjs(d).format("YYYY-MM-DD")
                );
                const draftDates = draftDatesByNsrId[row.nsrId] || originalDates;
                const draftSet = buildDateSet(draftDates);
                const readOnly = !row.staff;

                return (
                  <TableRow
                    key={row.nsrId}
                    hover={row.staff}
                    sx={
                      row.staff
                        ? undefined
                        : {
                            backgroundColor: "rgba(255, 193, 7, 0.08)",
                            "& td": { color: "text.secondary" },
                          }
                    }
                  >
                    <TableCell>
                      <Stack spacing={0.5}>
                        {row.adultId && onSelectAdult ? (
                          <Button
                            variant="text"
                            size="small"
                            onClick={() => onSelectAdult(row.adultId)}
                            sx={{
                              justifyContent: "flex-start",
                              p: 0,
                              minWidth: 0,
                              textTransform: "none",
                              fontWeight: 600,
                              color: readOnly ? "text.secondary" : "primary.main",
                            }}
                          >
                            {row.name || "-"}
                          </Button>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                            color={readOnly ? "text.secondary" : "text.primary"}
                          >
                            {row.name || "-"}
                          </Typography>
                        )}

                        <Typography variant= "caption" color="text.secondary">
                          NSR #{row.nsrId} · Adult #{row.adultId}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {row.staff ? (
                        <Chip size="small" label="Staff" color="success" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Not staff" color="warning" variant="outlined" />
                      )}
                    </TableCell>

                    <TableCell>
                      {row.roles?.length ? (
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          {row.roles.map((role) => (
                            <Chip
                              key={role}
                              size="small"
                              label={role}
                              variant={readOnly ? "outlined" : "filled"}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>{formatDisplayDate(row.arrivalDate)}</TableCell>
                    <TableCell>{formatDisplayDate(row.departureDate)}</TableCell>

                    {days.map((day) => {
                      const dayKey = dayjs(day).format("YYYY-MM-DD");
                      const withinStay = isWithinStay(day, row.arrivalDate, row.departureDate);
                      const checked = draftSet.has(dayKey);

                      return (
                        <TableCell
                          key={`${row.nsrId}-${day}`}
                          align="center"
                          sx={{
                            backgroundColor: withinStay ? "transparent" : "action.hover",
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={!withinStay || savingAll || readOnly}
                            onChange={() => toggleDate(row.nsrId, dayKey)}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5 + days.length}>
                    <Typography variant="body2" color="text.secondary">
                      No staff scheduling rows found for this week.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Edit all staff rows and save in one action. Non-staff entries are shown for reference only.
          </Typography>
        </Paper>
      </Stack>

      <CreateStaffDialog
        open={createDialogOpen}
        onClose={handleCloseCreateStaff}
        campuses={campuses}
        defaultCampus={selectedCampusName}
        onCreated={handleStaffCreated}
        showToast={showToast}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
          action={
            toast.adultId && onSelectAdult ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  const adultId = toast.adultId;
                  closeToast();
                  onSelectAdult(adultId);
                }}
              >
                Open adult
              </Button>
            ) : null
          }
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}